import dayjs from 'dayjs'
import { Field } from 'formik'
import get from 'lodash-es/get'
import React, { useMemo, useState } from 'react'

import type { UploadProps } from '@fourthwall/components'
import {
  Banner,
  ButtonClean,
  Input,
  InputUI,
  Subsection,
  Tabs,
  Toasts,
  Upload,
  VisuallyHidden,
  useMediaQueryContext,
} from '@fourthwall/components'
import { ExclamationMarkIcon, UploadIcon } from '@fourthwall/icons'
import { useFormikContext } from '@fourthwall/utils/dist/formik'

import { useDashboardFeatureFlags } from '../../../../../../../components/utils/DashboardFeatureFlags'
import { getRejectionErrors } from '../../../../../../../utils/dropzone'
import { RemovableUpload } from '../../../../components/RemovableUpload/RemovableUpload'
import VideoPlayer from '../../../../components/VideoPlayer'
import { ALLOWED_VIDEO_EXTENSIONS } from '../../../../consts'
import type { Post, VideoPostType } from '../../../../types'
import { isFirefox, isSafari } from '../../../../utils/browserDetect'
import { Overlay } from '../../components/Overlay'
import type { FormValues } from '../../types'
import * as S from './VideoFields.styled'
import { getVimeoEmbedURL, getVimeoVideoId, getYouTubeEmbedURL, getYouTubeVideoId } from './utils'
import { getStreamableEmbedURL } from './utils/getStreamableEmbedURL'
import { getStreamableVideoId } from './utils/getStreamableVideoId'
import { getVimeoLiveStreamEmbedURL } from './utils/getVimeoLiveStreamEmbedURL'
import { getVimeoLiveStreamId } from './utils/getVimeoLiveStreamId'
import { prepareSubtitles } from './utils/prepareSubtitles'

const TABS: VideoPostType[] = ['upload', 'embed']

type Props = {
  type: VideoPostType
  typeFieldName: string
  video?: string | File
  videoFieldName: string
  thumbnail?: string | File
  thumbnailFieldName: string
  subtitles?: boolean | File
  subtitlesFieldName?: string
  url?: string
  urlFieldName: string
  subsectionTitle?: string
  disabled?: boolean
  isUploading?: boolean
  uploadProgress?: number
  isThumbnailCustom?: boolean
  thumbnailUrl?: string | null
  status?: Post['statuses']['resources']
  updatedAt?: string
}

export const VideoFields = ({
  type,
  typeFieldName,
  video,
  videoFieldName,
  thumbnail,
  thumbnailFieldName,
  subtitles,
  subtitlesFieldName,
  url,
  urlFieldName,
  subsectionTitle,
  isUploading,
  uploadProgress,
  isThumbnailCustom,
  thumbnailUrl,
  disabled,
  status,
  updatedAt,
}: Props) => {
  const media = useMediaQueryContext()
  const { values, errors, setFieldValue } = useFormikContext<FormValues>()
  const [banner, setBanner] = useState<null | string>(null)
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const videoSrc = useMemo(() => {
    if (!video) {
      return undefined
    }

    if (typeof video === 'string') {
      return video
    }

    return URL.createObjectURL(video)
  }, [video])

  const thumbnailSrc = useMemo(() => {
    if (!thumbnail) {
      return undefined
    }

    if (typeof thumbnail === 'string') {
      return thumbnail
    }

    return URL.createObjectURL(thumbnail)
  }, [thumbnail])

  const handleVideoThumbnailUpload = (file?: File) => {
    if (file) {
      setFieldValue(thumbnailFieldName, file)
    }
  }

  const handleSubtitlesUpload = async (file?: File) => {
    if (file && subtitlesFieldName) {
      prepareSubtitles(file)
        .then((subtitlesFile) => {
          setFieldValue(subtitlesFieldName, subtitlesFile)
        })
        .catch(() => {
          Toasts.notify('Unable to process the subtitle file.')
        })
    }
  }

  const handleVideoUpload = (files?: File[]) => {
    setBanner(null)

    if (files?.[0]) {
      if (files[0].type === 'video/x-matroska' && (isSafari() || isFirefox())) {
        setBanner(
          'Instant preview of MKV files is not supported by Safari and Firefox browsers. Video will be available after processing.',
        )
      }
      setFieldValue(videoFieldName, files[0])
    }
  }

  const handleVideoRemove = () => {
    setBanner(null)
    setFieldValue(videoFieldName, undefined)
  }

  const renderThumbnailButton = () => {
    if (type === 'upload' && !video) {
      return null
    }

    if (type === 'embed' && !url) {
      return null
    }

    if (!thumbnail || !(isThumbnailCustom || typeof thumbnail === 'object')) {
      return (
        <div>
          <Upload
            accept={{
              'image/*': [],
            }}
            maxSize={10485760}
            // eslint-disable-next-line react/no-unstable-nested-components
            content={() => (
              <ButtonClean label="Upload custom thumbnail" disabled={isUploading || disabled} />
            )}
            onFileDropRejected={handleFileReject}
            onFileDrop={(files) => handleVideoThumbnailUpload(files?.[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <ButtonClean
          label="Remove custom thumbnail"
          disabled={isUploading || disabled}
          onClick={() => {
            setFieldValue(thumbnailFieldName, isThumbnailCustom ? undefined : thumbnailUrl)
          }}
        />
      </div>
    )
  }

  const renderSubtitlesButton = () => {
    if (!subtitlesFieldName) {
      return null
    }

    if (type === 'upload' && !video) {
      return null
    }

    if (type === 'embed' && !url) {
      return null
    }

    if (!subtitles) {
      return (
        <div>
          <Upload
            accept={{
              'text/vtt': ['.vtt'],
              'text/srt': ['.srt'],
            }}
            maxSize={10485760}
            // eslint-disable-next-line react/no-unstable-nested-components
            content={() => (
              <ButtonClean label="Upload custom subtitles" disabled={isUploading || disabled} />
            )}
            onFileDropRejected={handleFileReject}
            onFileDrop={(files) => handleSubtitlesUpload(files?.[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <ButtonClean
          label="Remove custom subtitles"
          disabled={isUploading || disabled}
          onClick={() => {
            setFieldValue(subtitlesFieldName, false)
          }}
        />
      </div>
    )
  }

  const renderOverlay = () => {
    if (status === 'Errored') {
      return (
        <Overlay
          type="error"
          showLoader={false}
          title="An error occured during video processing. Please re-upload your video."
          icon={ExclamationMarkIcon}
        />
      )
    }

    if (
      (status === 'Processing' || status === 'Created') &&
      updatedAt &&
      dayjs(updatedAt).isBefore(dayjs().subtract(3, 'hours'))
    ) {
      return (
        <Overlay
          type="error"
          showLoader={false}
          title="Video re-upload could not be processed. You may re-upload video again."
          icon={ExclamationMarkIcon}
        />
      )
    }

    if (status === 'Processing' || status === 'Created') {
      return (
        <Overlay
          title="Processing"
          subTitle="This process can take up to an hour, depending on the video file size."
        />
      )
    }
  }

  const renderUploadingOverlay = () => {
    const getMessage = () => {
      if (uploadProgress === 0) {
        return 'Uploading'
      }

      return `Uploading (${uploadProgress}%)`
    }

    return <Overlay title={getMessage()} />
  }

  const renderVideo = () => {
    if (!videoSrc) {
      return null
    }

    if (status === 'Processing' || status === 'Errored') {
      return <S.OverlayContainer>{renderOverlay()}</S.OverlayContainer>
    }

    return (
      <S.PlayerContainer>
        <RemovableUpload onRemove={handleVideoRemove}>
          <VideoPlayer
            videoSrc={videoSrc}
            thumbnailSrc={thumbnailSrc}
            hls={typeof video === 'string'}
          />
        </RemovableUpload>
        {isUploading && renderUploadingOverlay()}
      </S.PlayerContainer>
    )
  }

  const handleFileReject: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderUploader = () => {
    if (videoSrc) {
      return null
    }

    return (
      <S.UploadContainer>
        <Upload
          accept={ALLOWED_VIDEO_EXTENSIONS}
          title={
            media.tablet ? (
              <span>
                Drop video or <u>browse</u>
              </span>
            ) : (
              'Browse'
            )
          }
          maxSize={53687091200} // 50 GB
          invalid={!!get(errors, videoFieldName)}
          icon={UploadIcon}
          iconSize={20}
          orientation="horizontal"
          onFileDrop={handleVideoUpload}
          onFileDropRejected={handleFileReject}
          disabled={
            ['Public', 'FreeMembers', 'FreeAndPaidMembers'].includes(values.access) &&
            !dashboardFeatureFlags.membershipsPublicVideo.enabled
          }
        />
      </S.UploadContainer>
    )
  }

  const renderPublicPostAlert = () => {
    if (dashboardFeatureFlags.membershipsPublicVideo.enabled) return

    if (['Public', 'FreeMembers', 'FreeAndPaidMembers'].includes(values.access)) {
      return (
        <div style={{ marginBottom: 16 }}>
          <Banner
            title="Due to bandwidth costs, uploaded videos can only be offered to paid members. To share a video everyone can watch, use the ‘Embed video’ option."
            appearance="warning"
          />
        </div>
      )
    }
  }

  const renderUploadVideoSection = () => {
    return (
      <S.Container>
        {/* NOTE: Hack to focus whole section on submit with error */}
        <VisuallyHidden>
          <input type="text" name={videoFieldName} />
        </VisuallyHidden>
        {renderPublicPostAlert()}
        {renderVideo()}
        {renderUploader()}
        {get(errors, videoFieldName) && (
          <InputUI.Error>{get(errors, videoFieldName)}</InputUI.Error>
        )}
        <S.Buttons>
          {renderThumbnailButton()}
          {renderSubtitlesButton()}
        </S.Buttons>
        {banner && (
          <div style={{ marginTop: 16 }}>
            <Banner>{banner}</Banner>
          </div>
        )}
      </S.Container>
    )
  }

  const renderEmbedVideoSection = () => {
    const renderIFrame = (url: string) => (
      <S.IFrameContainer>
        <iframe src={url} width="100%" height="310" />
        {isUploading && renderUploadingOverlay()}
      </S.IFrameContainer>
    )

    const renderEmbedVideo = () => {
      if (url) {
        const youtubeVideoId = getYouTubeVideoId(url)
        const vimeoVideoData = getVimeoVideoId(url)
        const vimeoLiveStreamData = getVimeoLiveStreamId(url)
        const streamableVideoId = getStreamableVideoId(url)

        if (youtubeVideoId) {
          return renderIFrame(getYouTubeEmbedURL(youtubeVideoId))
        }

        if (vimeoVideoData?.id) {
          return renderIFrame(getVimeoEmbedURL(vimeoVideoData.id, vimeoVideoData.unlistedHash))
        }

        if (vimeoLiveStreamData?.id) {
          return renderIFrame(
            getVimeoLiveStreamEmbedURL(vimeoLiveStreamData.id, vimeoLiveStreamData.unlistedHash),
          )
        }

        if (streamableVideoId) {
          return renderIFrame(getStreamableEmbedURL(streamableVideoId))
        }
      }
    }

    return (
      <S.Container>
        <Field
          name={urlFieldName}
          placeholder="Video URL"
          component={Input}
          disabled={isUploading}
          suggestion="Supports YouTube, Vimeo and Streamable embeds"
        />
        {renderEmbedVideo()}
      </S.Container>
    )
  }

  if (status) {
    return (
      <Subsection title={subsectionTitle}>
        {type === 'embed' ? renderEmbedVideoSection() : renderUploadVideoSection()}
      </Subsection>
    )
  }

  return (
    <Subsection title={subsectionTitle}>
      <Tabs
        defaultIndex={TABS.indexOf(type)}
        onSelect={(index) => setFieldValue(typeFieldName, TABS[index])}
      >
        <Tabs.List>
          <Tabs.Item name="Upload" variant="dimmed" />
          <Tabs.Item name="Embed" variant="dimmed" />
        </Tabs.List>
        <Tabs.Panel>{renderUploadVideoSection()}</Tabs.Panel>
        <Tabs.Panel>{renderEmbedVideoSection()}</Tabs.Panel>
      </Tabs>
    </Subsection>
  )
}
